import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "preact/hooks";
import type { JSX } from "preact";
import type { Attachment, Message } from "../../api";
import { Avatar } from "../../components/Avatar";
import { PencilIcon, TrashIcon } from "../../components/Icons";
import { AttachmentList } from "../../components/Media";
import { clock } from "../../lib/format";
import { groupThread } from "../../lib/thread";
import { useI18n } from "../../lib/i18n";

/**
 * The conversation itself.
 *
 * Message bodies are rendered as text, with links built from the server's
 * offsets. This is the page that shows strings written by strangers, so it is
 * the last place that should ever be tempted into building markup from one.
 */

function Body({ message }: { message: Message }): JSX.Element {
  if (!message.links?.length) return <>{message.body}</>;
  const parts: JSX.Element[] = [];
  let cursor = 0;
  message.links.forEach((link, index) => {
    if (link.start > cursor) parts.push(<span key={`t${index}`}>{message.body.slice(cursor, link.start)}</span>);
    parts.push(
      <a key={`a${index}`} href={link.href} target="_blank" rel="noopener noreferrer nofollow">
        {message.body.slice(link.start, link.end)}
      </a>,
    );
    cursor = link.end;
  });
  if (cursor < message.body.length) parts.push(<span key="tail">{message.body.slice(cursor)}</span>);
  return <>{parts}</>;
}

/** Who may change a message: only what the team wrote, and only once the server has it. */
export function messageRights(
  message: Pick<Message, "id" | "kind" | "operatorId" | "pending" | "body">,
  meId: number,
  isAdmin: boolean,
): { edit: boolean; remove: boolean } {
  const settled = message.id > 0 && !message.pending;
  const ours = message.kind === "operator" || message.kind === "note";
  const mine = message.operatorId === meId;
  return {
    // An edit puts words in the author's mouth, so it is the author's alone —
    // and only for text: a message that is just a file has nothing to correct.
    edit: settled && ours && mine && message.body !== "",
    remove: settled && ours && (mine || isAdmin),
  };
}

export function Thread({
  messages,
  visitorName,
  onOpenImage,
  hasEarlier = false,
  loadingEarlier = false,
  onLoadEarlier,
  actions,
}: {
  messages: Message[];
  visitorName: string;
  onOpenImage: (item: Attachment) => void;
  /** There are older messages than the first one here. */
  hasEarlier?: boolean;
  loadingEarlier?: boolean;
  onLoadEarlier?: () => void;
  /** Absent where a thread is read-only (the history). */
  actions?: { meId: number; isAdmin: boolean; onEdit: (message: Message) => void; onDelete: (message: Message) => void };
}): JSX.Element {
  const { t: all, lang } = useI18n();
  const t = all.thread;
  const ref = useRef<HTMLDivElement>(null);
  const items = useMemo(() => groupThread(messages, Date.now(), lang), [messages, lang]);

  const scrollToEnd = useCallback(() => {
    const el = ref.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const last = messages[messages.length - 1];
  const lastKey = last ? `${last.conversationId}:${last.clientMsgId ?? last.id}` : "";
  const firstId = messages.find((m) => m.id > 0)?.id;

  // Older messages arriving above must not move what the operator is reading:
  // the view is pushed down by exactly the height that was added on top.
  const before = useRef<{ firstId: number | undefined; height: number; lastKey: string }>({
    firstId: undefined,
    height: 0,
    lastKey: "",
  });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const prev = before.current;
    const prepended =
      prev.firstId !== undefined && firstId !== undefined && firstId < prev.firstId && prev.lastKey === lastKey;
    if (prepended) el.scrollTop += el.scrollHeight - prev.height;
    before.current = { firstId, height: el.scrollHeight, lastKey };
  });

  // The thread follows what was said last — not an edit further up, and not
  // the page of older messages just loaded above.
  useEffect(() => {
    scrollToEnd();
  }, [lastKey, last?.progress, scrollToEnd]);

  return (
    <div class="thread" ref={ref} role="log" aria-live="polite" aria-label={t.label}>
      {hasEarlier && onLoadEarlier ? (
        <div class="earlier">
          <button class="btn sm" disabled={loadingEarlier} onClick={onLoadEarlier}>
            {loadingEarlier ? all.common.loading : all.conv.loadEarlier}
          </button>
        </div>
      ) : null}
      {items.map((item) => {
        if (item.type === "day") {
          return (
            <div key={item.key} class="day">
              {item.label}
            </div>
          );
        }

        const { message, first, last: lastOfGroup } = item;
        if (message.kind === "system") {
          return (
            <div key={item.key} class="msg system">
              <div class="bubble">{message.body}</div>
            </div>
          );
        }

        const team = message.kind !== "visitor";
        const author =
          message.kind === "bot" ? t.automatic : team ? (message.operator?.displayName ?? t.team) : visitorName;
        const rights = actions ? messageRights(message, actions.meId, actions.isAdmin) : { edit: false, remove: false };
        return (
          <div key={item.key} class={`msg ${message.kind}${team ? " mine" : ""}${first ? " first" : ""}`}>
            <div class="slot">
              {lastOfGroup ? (
                team ? (
                  <Avatar name={author} color={message.operator?.color} url={message.operator?.avatarUrl} size={28} />
                ) : (
                  <span class="avatar visitor" style={{ width: "28px", height: "28px", fontSize: "12px" }} aria-hidden="true">
                    {author.charAt(0).toLocaleUpperCase(lang)}
                  </span>
                )
              ) : null}
            </div>
            <div class="col">
              {first ? (
                <div class="name">
                  {message.kind === "note" ? <span class="note-tag">{t.note}</span> : null}
                  {author}
                </div>
              ) : null}
              <div class="bubble-row">
                {rights.edit || rights.remove ? (
                  <span class="msg-actions">
                    {rights.edit ? (
                      <button aria-label={t.editMessage} title={t.editMessage} onClick={() => actions!.onEdit(message)}>
                        <PencilIcon size={14} />
                      </button>
                    ) : null}
                    {rights.remove ? (
                      <button aria-label={t.deleteMessage} title={t.deleteMessage} onClick={() => actions!.onDelete(message)}>
                        <TrashIcon size={14} />
                      </button>
                    ) : null}
                  </span>
                ) : null}
                <div class="bubble-col">
                  {message.body ? (
                    <div class={`bubble${message.pending ? " pending" : ""}`}>
                      <Body message={message} />
                    </div>
                  ) : null}
                  <AttachmentList items={message.attachments} onOpen={onOpenImage} onLoad={scrollToEnd} />
                </div>
              </div>
              {message.pending && message.progress !== undefined ? (
                <div class="progress" role="progressbar" aria-label={t.uploading} aria-valuenow={Math.round(message.progress * 100)}>
                  <span style={{ width: `${Math.round(message.progress * 100)}%` }} />
                </div>
              ) : null}
              {lastOfGroup || message.editedAt ? (
                <div class="time">
                  {message.pending
                    ? t.sending
                    : `${clock(message.createdAt, lang)}${message.editedAt ? ` · ${t.edited}` : ""}${
                        team && message.kind !== "note" && message.readByVisitorAt ? "  ✓✓" : ""
                      }`}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
